/**
 * Custom score visualization for the composite recommendation score (0–100).
 *
 * Replaces the generic horizontal-progress-bar ScoreDial with a circular
 * arc + tick notches at the bullish/bearish thresholds (35 and 65). The
 * thresholds are the contract: cross 65 → bullish, dip below 35 → bearish.
 * Visualizing them as tick marks lets the user see *where they are relative
 * to the regime change*, not just an absolute number.
 *
 * The arc starts at the top (12 o'clock) and fills clockwise.
 */

const COLORS = {
  bull: "#10b981",   // emerald-500
  bear: "#f43f5e",   // rose-500
  neutral: "#64748b", // slate-500
  track: "rgb(30 41 59)", // slate-800
  tick: "rgb(71 85 105)", // slate-600
};

export default function ScoreRing({
  score,
  size = 88,
  stroke = 7,
}: {
  score: number;
  size?: number;
  stroke?: number;
}) {
  const pct = Math.max(0, Math.min(100, score));
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const filled = (pct / 100) * C;
  const center = size / 2;
  const color = pct >= 65 ? COLORS.bull : pct <= 35 ? COLORS.bear : COLORS.neutral;
  const textColor =
    pct >= 65 ? "text-emerald-300" : pct <= 35 ? "text-rose-300" : "text-slate-200";

  // Threshold ticks at 35 and 65 — short radial line bridging the inner / outer edge of the ring.
  const tickAt = (t: number) => {
    const angle = -90 + (t / 100) * 360;
    const rad = (angle * Math.PI) / 180;
    const inner = r - stroke / 2 - 2;
    const outer = r + stroke / 2 + 2;
    return {
      x1: center + inner * Math.cos(rad),
      y1: center + inner * Math.sin(rad),
      x2: center + outer * Math.cos(rad),
      y2: center + outer * Math.sin(rad),
    };
  };

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <g transform={`rotate(-90 ${center} ${center})`}>
          <circle cx={center} cy={center} r={r} fill="none" stroke={COLORS.track} strokeWidth={stroke} />
          <circle
            cx={center}
            cy={center}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${filled} ${C}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 600ms cubic-bezier(0.22, 1, 0.36, 1), stroke 200ms" }}
          />
        </g>
        {[35, 65].map((t) => {
          const pts = tickAt(t);
          return (
            <line
              key={t}
              x1={pts.x1}
              y1={pts.y1}
              x2={pts.x2}
              y2={pts.y2}
              stroke={COLORS.tick}
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`font-mono font-semibold tabular-nums ${textColor}`} style={{ fontSize: size * 0.32 }}>
          {pct.toFixed(0)}
        </span>
      </div>
    </div>
  );
}
